import { FC, useMemo, useState } from 'react'
import { AppTheme } from '@engyalo/delivery-ui-components'
import { useAppSelector } from '../hooks'
import { RootState } from '../redux/store'
import { readCachedTheme, resolveAppTheme } from '../utils/bootTheme'
import getUrlParams from '../utils/getUrlParams'

interface Props {
  children: React.ReactNode
}

/**
 * Chooses the palette every frame is painted with, boot frames included
 * (COM-1110).
 *
 * `state.defaultSlice.config` is `undefined` until `fetchConfig` resolves, and
 * no reducer writes a theme sooner (the `fetchConfig.pending` case records
 * only which load is newest), so this
 * component used to hand `AppTheme` an empty object for the whole config round
 * trip — up to four attempts, since `getConfig` retries three times at 1 s on
 * every error. `AppTheme` reads that as "no theme" and falls back to the UI
 * library's own `YaloLightPalette`, which is why the splash spinner was Yalo
 * purple. A config fetch that *fails* has no `rejected` case either, so that
 * palette was permanent on that path.
 *
 * Two sources now stand in, in order:
 *
 * 1. the palette this browser last saw for this storefront, read from
 *    `localStorage` during the first render;
 * 2. the neutral palette `resolveAppTheme` supplies underneath both, for the
 *    first-ever open and for any browser that refuses storage.
 *
 * `configTheme ?? cachedTheme` — the live config always wins once it lands.
 */
const WrappedAppTheme: FC<Props> = ({ children }) => {
  const configTheme = useAppSelector(
    (state: RootState) => state.defaultSlice.config?.theme,
  )

  /*
   * A lazy `useState` initialiser rather than an effect, and that is the whole
   * point of the cache: this runs *during* the first render, so the remembered
   * palette is inside the theme before anything paints. An effect would land
   * one commit later and reintroduce the flash it exists to remove.
   *
   * This component sits above `BrowserRouter`, so `window.location.pathname` is
   * the only way to reach the slug. `containers/Config.tsx` and `routes/index`
   * read it the same way.
   */
  const [cachedTheme] = useState(() =>
    readCachedTheme(getUrlParams(window.location.pathname).storeName),
  )

  /*
   * `AppTheme` memoises `createTheme(getTheme(mode, customTheme))` on
   * `[activeMode, customTheme]`, so a fresh object every render rebuilds the
   * whole MUI theme every render. The previous `theme || {}` had that shape and
   * escaped notice only because nothing re-renders this component today.
   */
  const customTheme = useMemo(
    () => resolveAppTheme(configTheme ?? cachedTheme),
    [configTheme, cachedTheme],
  )

  return <AppTheme customTheme={customTheme}>{children}</AppTheme>
}

export default WrappedAppTheme
